<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-gray-900 via-amber-900 to-orange-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-amber-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-orange-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-amber-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-amber-300">
            <i class="bx bx-chip mr-2"></i>Smart Dedicated Compute
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          SDC3 Series Cloud <span class="text-amber-400">Instances</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl">
          Dedicated compute instances with smart resource allocation, isolated environments, and enhanced performance for mission-critical applications requiring consistent performance and security isolation.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/pricing" class="bg-transparent border-2 border-amber-500 text-amber-300 hover:bg-amber-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-purchase-tag mr-2"></i>
            View Pricing
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          <div class="bg-amber-500/10 backdrop-blur-sm rounded-xl p-4 border border-amber-500/30">
            <div class="text-3xl font-bold text-amber-400 mb-1">100%</div>
            <div class="text-sm text-gray-400">Dedicated</div>
          </div>
          <div class="bg-amber-500/10 backdrop-blur-sm rounded-xl p-4 border border-amber-500/30">
            <div class="text-3xl font-bold text-amber-400 mb-1">Zero</div>
            <div class="text-sm text-gray-400">Noisy Neighbors</div>
          </div>
          <div class="bg-amber-500/10 backdrop-blur-sm rounded-xl p-4 border border-amber-500/30">
            <div class="text-3xl font-bold text-amber-400 mb-1">24/7</div>
            <div class="text-sm text-gray-400">Support</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/sdc3-series-hero.png" alt="SDC3 Series" class="w-full h-auto">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-amber-600 to-orange-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-shield-quarter text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Isolated</div>
              <div class="text-xs">Environment</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-orange-600 to-amber-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-tachometer text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Max</div>
              <div class="text-xs">Performance</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Features Section -->
<section class="py-20 bg-slate-950 border-y border-slate-800">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-amber-500/10 border border-amber-500/40 text-amber-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        SDC3 Series Features
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Dedicated Compute <span class="text-amber-400">Performance</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Designed for mission-critical applications with dedicated resources and enhanced security.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Dedicated Resources -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-amber-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Dedicated Resources</h3>
        <p class="text-slate-300 mb-4">
          Fully dedicated CPU, memory, and storage resources with no resource sharing.
        </p>
        <div class="flex items-center text-amber-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">100% isolated environment</span>
        </div>
      </div>

      <!-- Enhanced Security -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-orange-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-lock text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Enhanced Security</h3>
        <p class="text-slate-300 mb-4">
          Isolated environments with dedicated security controls and compliance features.
        </p>
        <div class="flex items-center text-amber-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Hardware-level isolation</span>
        </div>
      </div>

      <!-- Consistent Performance -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-yellow-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-tachometer text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Consistent Performance</h3>
        <p class="text-slate-300 mb-4">
          Guaranteed performance with no noisy neighbors or resource contention.
        </p>
        <div class="flex items-center text-amber-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Predictable performance</span>
        </div>
      </div>

      <!-- Customizable Configurations -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-blue-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-cog text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Customizable Configurations</h3>
        <p class="text-slate-300 mb-4">
          Flexible configurations tailored to your specific workload requirements.
        </p>
        <div class="flex items-center text-amber-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Tailored solutions</span>
        </div>
      </div>

      <!-- Performance Monitoring -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-green-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-bar-chart text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Performance Monitoring</h3>
        <p class="text-slate-300 mb-4">
          Advanced monitoring and analytics for optimal resource utilization.
        </p>
        <div class="flex items-center text-amber-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Real-time metrics</span>
        </div>
      </div>

      <!-- 24/7 Support -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-purple-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-support text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">24/7 Support</h3>
        <p class="text-slate-300 mb-4">
          Round-the-clock dedicated support for mission-critical applications.
        </p>
        <div class="flex items-center text-amber-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Priority support</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Pricing Plans Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-amber-500/10 border border-amber-500/40 text-amber-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        SDC3 Series Plans
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Dedicated <span class="text-amber-400">Configuration Options</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Choose the perfect dedicated configuration for your mission-critical workloads.
      </p>
    </div>

    <div class="max-w-7xl mx-auto">
      <div class="bg-slate-900/80 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gradient-to-r from-amber-600 to-orange-700 text-white">
              <tr>
                <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Instance</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">vCPU</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">RAM</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">SSD Storage</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Configuration</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-800/80">
              <!-- SDC3-4.30GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-4.30GB</h3>
                    <p class="text-sm text-slate-300">Basic Dedicated</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">4</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">30 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">125 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-slate-800 text-slate-100 text-xs font-bold px-3 py-1 rounded-full">Basic</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-8.60GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-8.60GB</h3>
                    <p class="text-sm text-slate-300">Standard Dedicated</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">8</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">60 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-blue-600/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full">Standard</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-12.90GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-12.90GB</h3>
                    <p class="text-sm text-slate-300">Standard Plus</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">12</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">90 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full">Standard Plus</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-16.120GB (Popular) -->
              <tr class="bg-amber-900/40 border-2 border-amber-500/70">
                <td class="px-6 py-4">
                  <div class="relative">
                    <div class="absolute -top-3 -left-3">
                      <span class="bg-gradient-to-r from-amber-600 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        Most Popular
                      </span>
                    </div>
                    <h3 class="text-lg font-bold text-white mt-3">SDC3-16.120GB</h3>
                    <p class="text-sm text-slate-300">High Performance</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">16</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">120 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-amber-500/20 text-amber-200 text-xs font-bold px-3 py-1 rounded-full">High Performance</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block shadow-lg">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-24.180GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-24.180GB</h3>
                    <p class="text-sm text-slate-300">High Performance Plus</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">24</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">180 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-orange-500/20 text-orange-200 text-xs font-bold px-3 py-1 rounded-full">HP Plus</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-32.240GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-32.240GB</h3>
                    <p class="text-sm text-slate-300">Enterprise Dedicated</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">32</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">240 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-indigo-500/20 text-indigo-200 text-xs font-bold px-3 py-1 rounded-full">Enterprise</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-48.360GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-48.360GB</h3>
                    <p class="text-sm text-slate-300">Enterprise Plus</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">48</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">360 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full">Enterprise Plus</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-64.480GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-64.480GB</h3>
                    <p class="text-sm text-slate-300">High Memory</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">64</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">480 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-pink-500/20 text-pink-200 text-xs font-bold px-3 py-1 rounded-full">High Memory</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- SDC3-96.720GB -->
              <tr class="hover:bg-amber-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">SDC3-96.720GB</h3>
                    <p class="text-sm text-slate-300">High Memory Plus</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">96</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">720 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">250 GB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-rose-500/20 text-rose-200 text-xs font-bold px-3 py-1 rounded-full">HM Plus</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="bg-white/5 backdrop-blur-xl rounded-xl p-6 mt-8 text-center border border-amber-500/30">
        <div class="flex items-center justify-center text-amber-100 mb-2">
          <i class="bx bx-info-circle text-2xl mr-2"></i>
          <span class="font-semibold">All plans include 24/7 support, automatic backups, and enterprise-grade security.</span>
        </div>
        <p class="text-sm text-slate-300">Contact us for custom configurations and enterprise solutions.</p>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-amber-600 via-orange-700 to-red-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-orange-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Deploy Dedicated Instances?
      </h2>
      <p class="text-xl text-gray-100 mb-10">
        Get started with our SDC3 Series cloud instances today and experience unmatched dedicated performance.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-amber-600 font-bold py-4 px-8 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-phone mr-2 text-xl"></i>
          <span class="text-lg">Contact Us</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-chat mr-2 text-xl"></i>
          <span class="text-lg">Talk to an Expert</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">100%</div>
          <div class="text-gray-200 text-sm">Dedicated</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">96</div>
          <div class="text-gray-200 text-sm">Max vCPU</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">720GB</div>
          <div class="text-gray-200 text-sm">Max RAM</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Zero</div>
          <div class="text-gray-200 text-sm">Noisy Neighbors</div>
        </div>
      </div>
    </div>
  </div>
</section>
